import {CONDITION_TYPES} from '../../pages/UserPages/Flow/WorkFlow/FlowCanvas/components/ConditionalSidebar'
import {
  DelayTime,
  DelayUnit,
  IChannel,
  IManualTab,
  ISection,
  ITriggerOption,
} from '../../pages/UserPages/Flow/WorkFlow/FlowTypes'
import {Node, type Edge} from 'reactflow'
import {getCurrentTimeWithTimezone} from '../_helpers/timezone'

// Enums
export enum TriggerType {
  MANUAL = 'manual',
  AUTOMATION = 'automation',
}

export enum MessageType {
  TOGGLE_PREVIEW = 'TOGGLE_PREVIEW',
  TYPE_CHANGED = 'TYPE_CHANGED',
  ADD_NODE_BETWEEN_EDGES = 'ADD_NODE_BETWEEN_EDGES',
}

export enum ManualTabKey {
  CUSTOM = 'custom',
  SEGMENTS = 'segments',
  TAGS = 'tags',
  ALL = 'all',
}

export enum NodeType {
  TRIGGER = 'trigger',
  END = 'end',
  CONDITION = 'condition',
  DELAY = 'delay',
  WAIT_FOR_REPLY = 'wait_for_reply',
  ADD_TAG = 'add_tag',
  EMAIL = 'email',
  WHATSAPP = 'whatsapp',
  MMS = 'mms',
  SMS = 'sms',
}

// Constants
export const REACT_FLOW_MIME_TYPE = 'application/reactflow'

export const EVENT_NAMES = {
  MESSAGE: 'message',
} as const

export const CHANNELS: readonly IChannel[] = [
  {
    type: 'Email',
    icon: 'bi-envelope',
    color: '#EA4335',
    svg: '/media/svg/automation/channels/email.svg',
    isVisible: false,
    altText: 'FLOW.SIDEBAR.CHANNELS.EMAIL',
  },
  {
    type: 'Whatsapp',
    icon: 'bi-whatsapp',
    color: '#25D366',
    svg: '/media/svg/automation/channels/whatsapp.svg',
    isVisible: false,
    altText: 'FLOW.SIDEBAR.CHANNELS.WHATSAPP',
  },
  {
    type: 'MMS',
    icon: 'bi-image',
    color: 'FF3D00',
    svg: '/media/svg/automation/channels/mms.svg',
    isVisible: false,
    bgColor: '#FF9A9A',
    altText: 'FLOW.SIDEBAR.CHANNELS.MMS',
  },
  {
    type: 'SMS',
    icon: 'bi-chat-dots',
    color: '4285F4',
    svg: '/media/svg/automation/channels/sms.svg',
    isVisible: true,
    bgColor: '#E8F0FF',
    altText: 'FLOW.SIDEBAR.CHANNELS.SMS',
  },
]

export const TIMING: readonly IChannel[] = [
  {
    type: 'Delay',
    icon: 'bi-clock',
    color: '#FF3D00',
    svg: '/media/svg/automation/channels/delay.svg',
    isVisible: true,
    bgColor: '#FF9A9A',
    altText: 'FLOW.SIDEBAR.TIMING.DELAY',
  },
  {
    type: 'Wait_for_reply',
    icon: 'bi-clock-history',
    color: '#4285F4',
    svg: '/media/svg/automation/channels/delay.svg',
    isVisible: true,
    bgColor: '#E8F0FF',
    altText: 'FLOW.SIDEBAR.TIMING.WAIT_FOR_REPLY',
  },
]

export const CONDITION: readonly IChannel[] = [
  {
    type: 'Condition',
    icon: 'bi-diagram-3',
    color: '#6F42C1',
    svg: '/media/svg/automation/channels/condition.svg',
    isVisible: true,
    bgColor: '#DBFFCB',
    altText: 'FLOW.SIDEBAR.LOGIC.CONDITION',
  },
]

export const LOGIC: readonly IChannel[] = [
  {
    type: 'Condition',
    icon: 'bi-diagram-3',
    color: '#6F42C1',
    svg: '/media/svg/automation/channels/condition.svg',
    isVisible: true,
    bgColor: '#DBFFCB',
    altText: 'FLOW.SIDEBAR.LOGIC.CONDITION',
  },
  {
    type: 'Add_tag',
    icon: 'bi-tag',
    color: '#0D6EFD',
    svg: '/media/svg/automation/contacts/addedTag.svg',
    isVisible: true,
    bgColor: '#E0F0FF',
    altText: 'FLOW.SIDEBAR.LOGIC.ADD_TAG',
  },
]

export const SECTIONS: readonly ISection[] = [
  {title: 'FLOW.SIDEBAR.CHANNELS', items: CHANNELS, moreInfo: 'FLOW.SIDEBAR.CHANNELS.MORE_INFO'},
  {title: 'FLOW.SIDEBAR.TIMING', items: TIMING, moreInfo: 'FLOW.SIDEBAR.TIMING.MORE_INFO'},
  {title: 'FLOW.SIDEBAR.LOGIC', items: LOGIC, moreInfo: 'FLOW.SIDEBAR.LOGIC.MORE_INFO'},
]

export const TRIGGER_TYPE_OPTIONS: readonly ITriggerOption[] = [
  {value: TriggerType.MANUAL, labelKey: 'FLOW.PREVIEW.OPTION.MANUAL'},
  {value: TriggerType.AUTOMATION, labelKey: 'FLOW.PREVIEW.OPTION.AUTOMATION'},
]

export const MANUAL_TABS: readonly IManualTab[] = [
  {key: ManualTabKey.CUSTOM, labelKey: 'FLOW.SIDEBAR.MANUAL_TAB.CUSTOM'},
  {key: ManualTabKey.SEGMENTS, labelKey: 'FLOW.SIDEBAR.MANUAL_TAB.SEGMENTS'},
  {key: ManualTabKey.TAGS, labelKey: 'FLOW.SIDEBAR.MANUAL_TAB.TAGS'},
  {key: ManualTabKey.ALL, labelKey: 'FLOW.SIDEBAR.MANUAL_TAB.ALL'},
]

export const AUTOMATION_AUDIENCE_OPTIONS = [
  {
    key: 'contact',
    value: 'added_contact',
    icon: 'bi-person-plus',
    titleKey: 'FLOW.SIDEBAR.AUTOMATION.ADDED_CONTACT.TITLE',
    descKey: 'FLOW.SIDEBAR.AUTOMATION.ADDED_CONTACT.DESC',
    svg: 'media/svg/automation/contacts/addedContact.svg',
  },
  {
    key: 'segment',
    value: 'added_segment',
    icon: 'bi-person-lines-fill',
    titleKey: 'FLOW.SIDEBAR.AUTOMATION.ADDED_SEGMENT.TITLE',
    descKey: 'FLOW.SIDEBAR.AUTOMATION.ADDED_SEGMENT.DESC',
    svg: 'media/svg/automation/contacts/addedSegment.svg',
  },
  {
    key: 'tag',
    value: 'added_tag',
    icon: 'bi-person-badge',
    titleKey: 'FLOW.SIDEBAR.AUTOMATION.ADDED_TAG.TITLE',
    descKey: 'FLOW.SIDEBAR.AUTOMATION.ADDED_TAG.DESC',
    svg: 'media/svg/automation/contacts/addedTag.svg',
  },
]

export const EDGE_TYPE = {
  CUSTOM: 'custom',
} as const

export const INITIAL_NODES: readonly Node[] = [
  {
    id: NodeType.TRIGGER,
    type: NodeType.TRIGGER,
    data: {
      metaData: {trigger: AUTOMATION_AUDIENCE_OPTIONS[0].value},
    },
    position: {x: 0, y: 0},
    deletable: false,
  },
  {
    id: NodeType.END,
    type: NodeType.END,
    data: {},
    position: {x: 105, y: 190},
    deletable: true,
  },
]

export const EDGE_TYPES = {
  BEZIER: 'bezier',
  STRAIGHT: 'straight',
  STEP: 'step',
  SMOOTHSTEP: 'smoothstep',
} as const

export const INITIAL_EDGES: readonly Edge[] = [
  {
    id: `${NodeType.TRIGGER}-${NodeType.END}`,
    source: NodeType.TRIGGER,
    target: NodeType.END,
    type: EDGE_TYPES.SMOOTHSTEP,
  },
]

export const FLOW_LAYOUT = {
  NODE_WIDTH: 100,
  NODE_HEIGHT: 50,
  NODE_X_OFFSET: 100,
  NODE_Y_OFFSET: 50,
  DEFAULT_DIRECTION: 'TB',
} as const

export const NODE_TYPE = {
  DEFAULT: 'default',
} as const

export const DROP_EFFECT = {
  MOVE: 'move',
} as const

export const REQUIRED_EXTENSIONS = {
  XHTML: 'http://www.w3.org/1999/xhtml',
} as const

export const EDGE_STYLE = {
  normal: {
    strokeWidth: 2,
    stroke: '#6c757d',
  },
  hover: {
    strokeWidth: 3,
    stroke: '#a084e8',
  },
}

export const EDGE_LABELS = {
  YES: 'yes',
  NO: 'no',
} as const

export const EDGE_LABELS_COLOR = {
  [EDGE_LABELS.YES]: '#2972BB',
  [EDGE_LABELS.NO]: '#5D5E60',
} as const

export const META_DATA_DEFAULT = {
  [NodeType.CONDITION]: [
    {
      type: CONDITION_TYPES[0].value,
      value: null,
    },
  ],
  [NodeType.DELAY]: {
    type: DelayTime.RELATIVE,
    value: 1,
    unit: DelayUnit.MINUTES,
    timeFromBrowser: getCurrentTimeWithTimezone(),
  },
  [NodeType.SMS]: null,
  [NodeType.WAIT_FOR_REPLY]: {
    windowMinutes: 60,
  },
  [NodeType.ADD_TAG]: null,
} as const

export const buildOptionsMetaData = (type: string) => {
  if (type === NodeType.SMS) {
    return {
      sender: '#SharedNum#',
      senderType: '#SharedNum#',
    }
  }
  return null
}
export enum LOGS_EVENT_TYPE {
  TRIGGER_COMPLETED = 'TRIGGER_COMPLETED',
  END_COMPLETED = 'END_COMPLETED',
  DELAY_STARTED = 'DELAY_STARTED',
  DELAY_COMPLETED = 'DELAY_COMPLETED',
  CONDITION_SENT = 'CONDITION_SENT',
  CONDITION_FAILED = 'CONDITION_FAILED',
  CONDITION_DELIVERED = 'CONDITION_DELIVERED',
  CONDITION_REPLY_CONTAIN = 'CONDITION_REPLY_CONTAIN',
  CONDITION_REPLY_START_WITH = 'CONDITION_REPLY_START_WITH',
  CONDITION_REPLY_END_WITH = 'CONDITION_REPLY_END_WITH',
  CONDITION_EQUAL = 'CONDITION_EQUAL',
  SMS_SENT = 'SMS_SENT',
  SMS_FAILED = 'SMS_FAILED',
  SMS_DELIVERED = 'SMS_DELIVERED',
  SMS_REPLIED = 'SMS_REPLIED',
  SMS_REPLIED_CONTAIN = 'SMS_REPLIED_CONTAIN',
  SMS_REPLIED_START_WITH = 'SMS_REPLIED_START_WITH',
  SMS_REPLIED_END_WITH = 'SMS_REPLIED_END_WITH',
  SMS_REPLIED_EQUAL = 'SMS_REPLIED_EQUAL',
  INSUFFICIENT_CREDIT = 'INSUFFICIENT_CREDIT',
  ADDED_TAG = 'added_tag',
  RUNNING = 'RUNNING',
  PAUSED = 'PAUSED',
  CANCELLED = 'CANCELLED',
  STOP_TO_REPLY = 'STOP_TO_REPLY',
  EMAIL_SENT = 'EMAIL_SENT',
  WAIT_FOR_REPLY_STARTED = 'WAIT_FOR_REPLY_STARTED',
  WAIT_FOR_REPLY_RECEIVED = 'WAIT_FOR_REPLY_RECEIVED',
  WAIT_FOR_REPLY_TIMEOUT = 'WAIT_FOR_REPLY_TIMEOUT',
  WAIT_FOR_REPLY_RESUMED = 'WAIT_FOR_REPLY_RESUMED',
  ADD_TAG_COMPLETED = 'ADD_TAG_COMPLETED',
  ADD_TAG_FAILED = 'ADD_TAG_FAILED',
  ADD_TAG_SKIPPED = 'ADD_TAG_SKIPPED',
}

export const EVENT_NAMES_MAP: Record<LOGS_EVENT_TYPE, string> = {
  [LOGS_EVENT_TYPE.TRIGGER_COMPLETED]: 'Trigger Completed',
  [LOGS_EVENT_TYPE.END_COMPLETED]: 'End Completed',
  [LOGS_EVENT_TYPE.DELAY_STARTED]: 'Delay Started',
  [LOGS_EVENT_TYPE.DELAY_COMPLETED]: 'Delay Completed',
  [LOGS_EVENT_TYPE.CONDITION_SENT]: 'Condition Sent',
  [LOGS_EVENT_TYPE.CONDITION_FAILED]: 'Condition Failed',
  [LOGS_EVENT_TYPE.CONDITION_DELIVERED]: 'Condition Delivered',
  [LOGS_EVENT_TYPE.CONDITION_REPLY_CONTAIN]: 'Condition Reply Contain',
  [LOGS_EVENT_TYPE.CONDITION_REPLY_START_WITH]: 'Condition Reply Start With',
  [LOGS_EVENT_TYPE.CONDITION_REPLY_END_WITH]: 'Condition Reply End With',
  [LOGS_EVENT_TYPE.CONDITION_EQUAL]: 'Condition Equal',
  [LOGS_EVENT_TYPE.SMS_SENT]: 'SMS Sent',
  [LOGS_EVENT_TYPE.SMS_FAILED]: 'SMS Failed',
  [LOGS_EVENT_TYPE.SMS_DELIVERED]: 'SMS Delivered',
  [LOGS_EVENT_TYPE.SMS_REPLIED]: 'SMS Replied',
  [LOGS_EVENT_TYPE.SMS_REPLIED_CONTAIN]: 'SMS Replied Contain',
  [LOGS_EVENT_TYPE.SMS_REPLIED_START_WITH]: 'SMS Replied Start With',
  [LOGS_EVENT_TYPE.SMS_REPLIED_END_WITH]: 'SMS Replied End With',
  [LOGS_EVENT_TYPE.SMS_REPLIED_EQUAL]: 'SMS Replied Equal',
  [LOGS_EVENT_TYPE.INSUFFICIENT_CREDIT]: 'Insufficient Credit',
  [LOGS_EVENT_TYPE.ADDED_TAG]: 'Added Tag',
  [LOGS_EVENT_TYPE.RUNNING]: 'Running',
  [LOGS_EVENT_TYPE.PAUSED]: 'Paused',
  [LOGS_EVENT_TYPE.CANCELLED]: 'Cancelled',
  [LOGS_EVENT_TYPE.STOP_TO_REPLY]: 'Stop To Reply',
  [LOGS_EVENT_TYPE.EMAIL_SENT]: 'Email Sent',
  [LOGS_EVENT_TYPE.WAIT_FOR_REPLY_STARTED]: 'Wait For Reply Started',
  [LOGS_EVENT_TYPE.WAIT_FOR_REPLY_RECEIVED]: 'Wait For Reply Received',
  [LOGS_EVENT_TYPE.WAIT_FOR_REPLY_TIMEOUT]: 'Wait For Reply Timeout',
  [LOGS_EVENT_TYPE.WAIT_FOR_REPLY_RESUMED]: 'Wait For Reply Resumed',
  [LOGS_EVENT_TYPE.ADD_TAG_COMPLETED]: 'Add Tag Completed',
  [LOGS_EVENT_TYPE.ADD_TAG_FAILED]: 'Add Tag Failed',
  [LOGS_EVENT_TYPE.ADD_TAG_SKIPPED]: 'Add Tag Skipped',
}

export interface IFlowLog {
  _id: string
  type: string
  status: string
  message: string
  data?: {
    nodeId?: string
    [key: string]: unknown
  }
  triggerEvent?: {
    type: string
    contactId: string
    contact: IContact
    number: string
    timestamp: string
    [key: string]: unknown
  }
  flowName?: string
  name?: string
  flowId?: string
  instanceId?: string
  currentNodeId?: string
  createdAt: string
  updatedAt?: string
  timestamp: string
  id?: string
}

export interface IContact {
  analytic?: {
    countrycode?: string
    [key: string]: unknown
  }
  tags?: string[]
  optIn?: boolean
  sendstatus?: boolean
  isGhlContact?: boolean
  isjobAdderContact?: boolean
  isHubspotContact?: boolean
  bounced?: number
  countryCode?: string
  isBlocked?: boolean
  isBlockedByUser?: boolean
  isFlagged?: boolean
  isArchived?: boolean
  isTicked?: boolean
  isInboxRead?: boolean
  addedThrough?: string
  _id: string
  contact?: string
  group?: string
  user?: string
  name?: string
  number?: string
  fields?: IContactField[]
  createdFrom?: string
  creatorName?: string
  __enc_number?: boolean
  createdAt?: string
  updatedAt?: string
  __v?: number
  [key: string]: unknown
}

export interface IContactField {
  _id: string
  name: string
  value: string
  length: number
  isEnglish: boolean
}

export enum LOGS_STATUS {
  COMPLETED = 'completed',
  PENDING = 'pending',
  RUNNING = 'running',
  WAITING = 'waiting',
  FAILED = 'failed',
  PAUSED = 'paused',
  CANCELLED = 'cancelled',
}

export enum TriggerEventType {
  ADDED_CONTACT = 'added_contact',
  ADDED_SEGMENT = 'added_segment',
  ADDED_TAG = 'added_tag',
}

export interface ITriggerEvent {
  type: TriggerEventType
  contactId: string
  contactName: string
  number: string
  timestamp: string | Date
  contact: Record<string, unknown>
}

export interface IContext {
  contactId: string
  userId: string
  addedThrough: string
  triggerType: TriggerEventType
}

export interface IFlowInstance {
  _id: string
  status: LOGS_STATUS
  flowId: string
  triggerEvent: ITriggerEvent
  currentNodeId: string
  nodeStatus: Record<string, string>
  context: IContext
  user: string
  flow?: IFlowLog
  createdAt: string | Date
  updatedAt: string | Date
  readyAt: string | Date
  eventLogs: IEventLog[]
  isReplies?: boolean
}

export interface IEventLog {
  message?: string
  type?: LOGS_EVENT_TYPE
  timestamp?: string | Date
  data?: Record<string, unknown>
}

export interface IFlowStatisticsStatusBreakdown {
  pending: number
  running: number
  waiting: number
  completed: number
  failed: number
  cancelled: number
  paused: number
}

export interface IFlowStatistics {
  totalInstances: number
  totalCustomers: number
  totalBites: number
  activeCustomers: number
  completedCustomers: number
  failedCustomers: number
  statusBreakdown: IFlowStatisticsStatusBreakdown
}

export interface IFlowStatisticsSummary {
  flowId: string
  flowName: string
  flowStatus: string
  statistics: IFlowStatistics
}
